import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    loadFeedback?: () => void;
  }
}

type T_props = {
  apiKey?: string;
  code?: string;
  triggers?: {
    exitIntent?: boolean;
    timeOnPage?: number; // in seconds
    forceVisibility?: boolean; // force visibility trigger
  };
  useExistingInstance?: boolean;
};

export const CemtrickScript = ({
  apiKey,
  code,
  triggers: {
    exitIntent = false,
    timeOnPage = 0,
    forceVisibility = false,
  } = {},
  useExistingInstance = false,
}: T_props) => {
  const [init, setInit] = useState(false);
  const triggeredByExitIntent = useRef(false);
  const triggeredByTimeOnPage = useRef(false);
  const triggeredForceVisibility = useRef(false);

  const isInterceptorSubmitted = () => {
    return !!sessionStorage.getItem(`interceptor-1.0.0-id`);
  };

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    const mouseLeaveHandler = function (e: MouseEvent) {
      // Only trigger when mouse leaves at the top
      if (
        e.clientY <= 0 &&
        !triggeredByExitIntent.current &&
        window?.loadFeedback &&
        !isInterceptorSubmitted()
      ) {
        timerId = setTimeout(function () {
          window?.loadFeedback && window.loadFeedback();
          triggeredByExitIntent.current = true;
        }, 500); // Delay to avoid immediate trigger
      }
    };

    const mouseEnterHandler = function () {
      clearTimeout(timerId);
    };

    if (init && exitIntent) {
      document.addEventListener("mouseleave", mouseLeaveHandler);
      document.addEventListener("mouseenter", mouseEnterHandler);
    }

    return () => {
      if (exitIntent) {
        document.removeEventListener("mouseleave", mouseLeaveHandler);
        document.removeEventListener("mouseenter", mouseEnterHandler);
      }
    };
  }, [init]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (init && timeOnPage > 0) {
      timerId = setTimeout(() => {
        if (
          !triggeredByTimeOnPage.current &&
          window?.loadFeedback &&
          !isInterceptorSubmitted()
        ) {
          window?.loadFeedback();
          triggeredByTimeOnPage.current = true;
        }
      }, timeOnPage * 1000); // Convert seconds to milliseconds
    }

    return () => {
      if (timeOnPage > 0) {
        clearTimeout(timerId);
      }
    };
  }, [init]);

  useEffect(() => {
    if (
      init &&
      forceVisibility &&
      !triggeredForceVisibility.current &&
      window?.loadFeedback &&
      !isInterceptorSubmitted()
    ) {
      window?.loadFeedback();
      triggeredForceVisibility.current = true;
    }
  }, [init, forceVisibility]);

  useEffect(() => {
    const inlineScript = document.createElement("script");
    inlineScript.setAttribute(
      "src",
      `https://interceptor.cemtrik.com/interceptor.js?code=${code}&key=${apiKey}`
    );

    if (!useExistingInstance) {
      document.body.appendChild(inlineScript);
    }

    // Fired only once
    setInit(true);

    return () => {
      if (!useExistingInstance) {
        document.body.removeChild(inlineScript);

        // Create a CSS selector to catch the cemtrick component <div style="position: absolute; z-index: 99998;"></div>
        document.querySelector('div[style*="z-index: 99998"]')?.remove();
      }
    };
  }, []);

  return null;
};
